import { log } from "evlog"
import {
  PaywallTemplateV1,
  PaywallTemplateV2,
  PaywallTemplateV3,
  PaywallTemplateV4,
  PaywallTemplateV5,
  PaywallTemplateV6,
  PaywallTemplateV7,
  PaywallTemplateV8,
  PaywallTemplateV9,
  PaywallTemplateV10,
  PaywallTemplateV11,
  PaywallTemplateV12,
} from "app/schema/paywallTemplate/paywallTemplate"
import { TElement } from "../../../schema/paywallTemplate/element/element"
import { z } from "zod"
import { Sub } from "@superwall-me/schema"
import {
  createDrawerAndProductsComponents,
  footerSubstitutionsMigrationScript,
  productDrawerSubstitutionMigrationScript,
  productsSwElementHasMigrationScript,
  visibilityNoneToDisplayNoneMigrationScript,
} from "./helpers/productDrawerSubstitutionMigrationScript"
import {
  drawerChildrenMigrationScripts,
  migrateProductsSubstitutions,
} from "./helpers/drawerChildrenMigrationScripts"

const schemas = {
  1: PaywallTemplateV1,
  2: PaywallTemplateV2,
  3: PaywallTemplateV3,
  4: PaywallTemplateV4,
  5: PaywallTemplateV5,
  6: PaywallTemplateV6,
  7: PaywallTemplateV7,
  8: PaywallTemplateV8,
  9: PaywallTemplateV9,
  10: PaywallTemplateV10,
  11: PaywallTemplateV11,
  12: PaywallTemplateV12,
}

const transformers = {
  /**
   * Migrate from version 1 to version 2
   *
   * - Bumps version to 2
   */
  1: (
    templateJson: z.infer<typeof PaywallTemplateV1>,
    substitutions: Sub[]
  ): { templateJson: z.infer<typeof PaywallTemplateV2>; substitutions: Sub[] } => {
    return {
      templateJson: {
        version: 2,
        pageSettings: { ...templateJson.pageSettings, showProductCheckMark: "false" },
        mainContent: templateJson.mainContent,
        cardContent: templateJson.cardContent,
      },
      substitutions,
    }
  },
  /**
   * Migration from version 2 to version 3
   *
   * - Bumps version to 3
   * - Adds show/hide checkmark
   * - Adds checkmark margin
   * - Adds product checkmark image
   */
  2: (
    templateJson: z.infer<typeof PaywallTemplateV2>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV3>
    substitutions: Sub[]
  } => {
    const showProductCheckMark =
      templateJson?.pageSettings?.showProductCheckMark === "true" ? "show" : "hide"
    return {
      templateJson: {
        version: 3,
        pageSettings: {
          ...templateJson.pageSettings,
          showProductCheckMark,
          selectedCheckBoxImage: "https://static.superwallassets.com/uoQsDwmrnamiqkfkzRKvC",
          unselectedCheckBoxImage: "https://static.superwallassets.com/ElRGbHkiRQTvFQ3Gojbfu",
          checkMarkMargin: "0 12px 0 4px",
        },
        mainContent: templateJson.mainContent,
        cardContent: templateJson.cardContent,
      },
      substitutions,
    }
  },
  /**
   * Migration from version 3 to version 4
   *
   * - Bumps version to 4
   * - Adds show/hide purchase button line two option
   */
  3: (
    templateJson: z.infer<typeof PaywallTemplateV3>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV4>
    substitutions: Sub[]
  } => {
    const addInitialStylesToDivs = (elements: TElement[]) => {
      const updateDiv = (elements: TElement[]) => {
        return elements.map((element) => {
          if (element.name === "div") {
            if (!element?.settings?.initialStyles) {
              return {
                ...element,
                settings: {
                  ...element.settings,
                  initialStyles: {},
                },
              }
            }
            return element
          }
          if (element.children) {
            return {
              ...element,
              children: updateDiv(Array.from(element.children)),
            }
          }
          return element
        })
      }
      return updateDiv(elements)
    }
    return {
      templateJson: {
        version: 4,
        pageSettings: {
          ...templateJson.pageSettings,
          purchaseButtonLineTwo: "show",
        },
        backgroundContent: [],
        mainContent: addInitialStylesToDivs(templateJson.mainContent || []),
        cardContent: addInitialStylesToDivs(templateJson.cardContent || []),
      },
      substitutions,
    }
  },
  /**
   * Migration from version 4 to version 5
   *
   * - Bumps version to 5
   * - Adds aspect ratio to video component settings
   */
  4: (
    templateJson: z.infer<typeof PaywallTemplateV4>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV5>
    substitutions: Sub[]
  } => {
    const addAspectSize = (elements: TElement[]) => {
      const updateVideo = (elements: TElement[]) => {
        return elements.map((element) => {
          if (element.name === "video") {
            if (!element?.settings?.aspectSize) {
              return {
                ...element,
                settings: {
                  ...element.settings,
                  aspectSize: "contain",
                },
              }
            }
            if (element?.settings?.aspectRatio) {
              return {
                ...element,
                settings: {
                  ...element.settings,
                  aspectSize: element.settings.aspectRatio,
                },
              }
            }
            return element
          }
          if (element.children) {
            return {
              ...element,
              children: updateVideo(Array.from(element.children)),
            }
          }
          return element
        })
      }
      return updateVideo(elements)
    }
    return {
      templateJson: {
        version: 5,
        pageSettings: templateJson.pageSettings,
        backgroundContent: addAspectSize(templateJson.backgroundContent || []),
        mainContent: addAspectSize(templateJson.mainContent || []),
        cardContent: addAspectSize(templateJson.cardContent || []),
      },
      substitutions,
    }
  },
  /**
   * Migration from version 5 to version 6
   *
   * - Bumps version to 6
   * - Adds `isParagraph`, `initialStyles`, and `text` to text component settings
   */
  5: (
    templateJson: z.infer<typeof PaywallTemplateV5>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV6>
    substitutions: Sub[]
  } => {
    const addTextSettings = (elements: TElement[]) => {
      const updateText = (elements: TElement[]) => {
        return elements.map((element) => {
          if (element.name === "text") {
            if (!element?.settings) {
              return {
                ...element,
                settings: {
                  isParagraph: false,
                  initialStyles: {},
                  text: "Text",
                },
              }
            }
            return element
          }
          if (element.children) {
            return {
              ...element,
              children: updateText(Array.from(element.children)),
            }
          }
          return element
        })
      }
      return updateText(elements)
    }
    return {
      templateJson: {
        version: 6,
        pageSettings: templateJson.pageSettings,
        backgroundContent: addTextSettings(templateJson.backgroundContent || []),
        mainContent: addTextSettings(templateJson.mainContent || []),
        cardContent: addTextSettings(templateJson.cardContent || []),
      },
      substitutions,
    }
  },
  /**
   * Migration from version 6 to version 7
   *
   * - Bumps version to 7
   * - Adds hasItemPadding to list component settings
   */
  6: (
    templateJson: z.infer<typeof PaywallTemplateV6>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV7>
    substitutions: Sub[]
  } => {
    const updateListSettings = (elements: TElement[]) => {
      const updateList = (elements: TElement[]) => {
        return elements.map((element) => {
          if (element.name === "list") {
            if (!element?.settings) {
              return {
                ...element,
                settings: {
                  ...element.settings,
                  HashChangeEvent: false,
                },
              }
            }
            return element
          }
          if (element.children) {
            return {
              ...element,
              children: updateList(Array.from(element.children)),
            }
          }
          return element
        })
      }
      return updateList(elements)
    }
    return {
      templateJson: {
        version: 7,
        pageSettings: templateJson.pageSettings,
        backgroundContent: updateListSettings(templateJson.backgroundContent || []),
        mainContent: updateListSettings(templateJson.mainContent || []),
        cardContent: updateListSettings(templateJson.cardContent || []),
      },
      substitutions,
    }
  },
  /**
   * Migration from version 7 to version 8
   *
   * - Bumps version to 8
   * - Adds hasItemPadding to list component settings
   */
  7: (
    templateJson: z.infer<typeof PaywallTemplateV7>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV8>
    substitutions: Sub[]
  } => {
    return {
      templateJson: {
        version: 8,
        pageSettings: {
          ...templateJson.pageSettings,
          hideFixedPurchaseDrawer: "false",
        },
        backgroundContent: templateJson.backgroundContent || [],
        mainContent: templateJson.mainContent || [],
        cardContent: templateJson.cardContent || [],
      },
      substitutions,
    }
  },
  /**
   * Migration from version 8 to version 9
   *
   * - Bumps version to 9
   * - Converts Products "reverseDirection" setting from string to boolean
   */
  8: (
    templateJson: z.infer<typeof PaywallTemplateV8>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV9>
    substitutions: Sub[]
  } => {
    const updateProductsSetting = (elements: TElement[]) => {
      const updateSetting = (elements: TElement[]) => {
        return elements.map((element) => {
          if (element.name === "products") {
            return {
              ...element,
              settings: {
                ...element.settings,
                reverseDirection: element.settings.reverseDirection === "true" ? true : false,
              },
            }
          }
          if (element.children) {
            return {
              ...element,
              children: updateSetting(Array.from(element.children)),
            }
          }
          return element
        })
      }
      return updateSetting(elements)
    }
    return {
      templateJson: {
        version: 9,
        pageSettings: templateJson.pageSettings,
        backgroundContent: templateJson.backgroundContent || [],
        mainContent: updateProductsSetting(templateJson.mainContent || []),
        cardContent: templateJson.cardContent || [],
      },
      substitutions,
    }
  },
  /**
   * Migration from version 9 to version 10
   *
   * - Bumps version to 10
   * - Adds default value to "pageSettings.backgroundContentMaxWidth"
   */
  9: (
    templateJson: z.infer<typeof PaywallTemplateV9>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV10>
    substitutions: Sub[]
  } => {
    return {
      templateJson: {
        version: 10,
        pageSettings: {
          ...templateJson.pageSettings,
          backgroundContentMaxWidth: "paywallMaxWidth",
        },
        backgroundContent: templateJson.backgroundContent || [],
        mainContent: templateJson.mainContent || [],
        cardContent: templateJson.cardContent || [],
      },
      substitutions,
    }
  },
  /**
   * Migration from version 10 to version 11
   *
   * - Bumps version to 11
   * - Wraps all components above the products section, but inside the drawer,
   * within a new Div component.
   */
  10: (
    data: z.infer<typeof PaywallTemplateV10>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV11>
    substitutions: Sub[]
  } => {
    const newMainContent = drawerChildrenMigrationScripts(data.mainContent || [])
    return {
      templateJson: {
        version: 11,
        pageSettings: data.pageSettings,
        backgroundContent: data.backgroundContent || [],
        mainContent: newMainContent,
        cardContent: data.cardContent || [],
      },
      substitutions: migrateProductsSubstitutions(substitutions, newMainContent),
    }
  },
  /**
   * Migration from version 11 to version 12
   *
   * - Bumps version to 12
   * - Sets "pageSettings.hideFixedPurchaseDrawer" to "true" for all
   * - Migrates legacy footer substitutions to new footer component subs
   * - Migrates legacy product drawer substitutions to new drawer and products component subs
   * - Creates footer, drawer and products components if they don't exist
   */
  11: (
    templateJson: z.infer<typeof PaywallTemplateV11>,
    substitutions: Sub[]
  ): {
    templateJson: z.infer<typeof PaywallTemplateV12>
    substitutions: Sub[]
  } => {
    const migratedSubs = visibilityNoneToDisplayNoneMigrationScript(
      productDrawerSubstitutionMigrationScript(
        footerSubstitutionsMigrationScript(substitutions, templateJson?.pageSettings || {}),
        templateJson?.pageSettings || {}
      )
    )
    const isUsingNewDrawer = templateJson?.pageSettings?.hideFixedPurchaseDrawer === "true"
    return {
      templateJson: {
        version: 12,
        pageSettings: {
          ...templateJson.pageSettings,
          customCSS: productsSwElementHasMigrationScript(
            templateJson?.pageSettings?.customCSS || ""
          ),
          hideFixedPurchaseDrawer: "true",
        },
        backgroundContent: templateJson.backgroundContent || [],
        mainContent: isUsingNewDrawer
          ? templateJson.mainContent || []
          : [
              ...(templateJson.mainContent || []),
              ...createDrawerAndProductsComponents(
                templateJson.pageSettings || {},
                templateJson.cardContent
              ),
            ],
        cardContent: templateJson.cardContent || [],
      },
      substitutions: migratedSubs,
    }
  },
}

const versionSchema = z.object({
  version: z.number(),
})

export const loadTemplate = (_data: any, substitutions: Sub[], maxMigrationVersion?: number) => {
  const data = !!_data.version ? _data : { version: 1, ..._data }
  const { version } = versionSchema.parse(data)
  // If there is a transformer, implying stale version, transform it
  const transformer = transformers[version]

  if (
    (transformer && maxMigrationVersion == undefined) ||
    (transformer && maxMigrationVersion != undefined && version < maxMigrationVersion)
  ) {
    log.info(
      "templateJsonMigration",
      `migrating template json from v${version || 0} to v${version + 1}`
    )
    const { templateJson, substitutions: newSubs } = transformer(data, substitutions)
    return loadTemplate(templateJson, newSubs, maxMigrationVersion)
  }

  try {
    schemas[version].parse(data)
    log.info("templateJsonMigration", `template json schema at v${version}`)
    return { templateJson: data, substitutions }
  } catch (e) {
    log.error({ error: String(e), message: "template json schema migration failed" })
    return { templateJson: data, substitutions }
  }
}
